import React from 'react';
import { get } from '@docavenue/core';
import {
  Appointment,
  CardHcd,
  VideoSessionOutputDTO,
} from '@maiia/model/generated/model/api-patient/api-patient';
import { useGet as useGetVideoSessionWaitingPosition } from '@maiia/model/generated/model/api-patient/hooks/patVideoSessionsWaitingPositionByVideoSessionId';
import { useSelector } from 'react-redux';
import {
  Heading,
  Link,
  Typography,
  cn,
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  toast,
} from '@kiiwi/ui';
import dayjs from 'dayjs';
import { usePut as usePutSpecialityByVideoSessionId } from '@maiia/model/generated/model/api-patient/hooks/patVideoSessionsSpecialityByVideoSessionId';
import { Trans, useTranslation } from '../../../../src/i18n';
import Avatar from '../../../atoms/Avatar/Avatar';
import { getAvatar, getCardFullName, getSubstitute } from '../../../utils';
import useTlcAmcSpecialties from '@/src/hooks/useTlcAmcSpecialties';
import { getIsDelayed, getStringDelay } from '@/components/utils/appointment';
import FontAwesomeIcon from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';

const ALLOW_SWITCH_TO_MEDECIN_GENERALISTE_AFTER_X_MIN = 15;

/**
 *  TODO
 *  remove: "hideDelay", "hideAvatar" & "size" props
 *  use only "isMiniOpenTok" instead
 *  */
type Props = {
  cardHcd: CardHcd;
  appointment?: Appointment;
  isAmcTlc?: boolean;
  isMiniOpenTok?: boolean;
  hideDelay?: boolean;
  hideAvatar?: boolean;
  size?: 'default' | 'large';
  videoSessionId: string;
};

type TlcAmcContentProps = {
  hideAvatar?: boolean;
  size?: 'default' | 'large';
};

const TlcAmcContent = ({ hideAvatar, size }: TlcAmcContentProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        size === 'large' && 'gap-3',
      )}
    >
      <Heading
        variant="subtitle-3"
        as="h1"
        className={cn(
          'flex items-center gap-2 text-center text-neutral-primary',
          size === 'large' && 'text-xl font-semibold',
        )}
      >
        <FontAwesomeIcon
          name="spinner-third:duotone"
          className="h-4 w-4 animate-spin text-[--kiiwi-kiiwi-450]"
        />
        {t('amc-tlc-waiting-room-title')}
      </Heading>
      <Typography
        className={cn(
          'text-sm text-neutral-secondary',
          size === 'large' && 'text-base',
        )}
      >
        {t('amc-tlc-waiting-room-subtitle')}
      </Typography>
      {!hideAvatar && (
        <img
          src="/static/images/tlc_amc/waiting-center.svg"
          alt=""
          aria-hidden
          className="max-w-[150px]"
        />
      )}
      <Typography
        variant="body-2"
        className="pt-2 text-sm text-neutral-secondary [&>*]:text-sm"
      >
        <Trans
          i18nKey="amc-tlc-waiting-room-non-mg-note"
          components={{
            a: (
              <Link
                href="https://maiia-guide.zendesk.com/hc/fr/articles/13000219888786-Sp%C3%A9cialistes-quand-les-consulter-en-T%C3%A9l%C3%A9consultation-24-7"
                target="_blank"
                rel="noreferrer"
                className="font-semibold !text-information-link-enabled underline"
              />
            ),
          }}
        />
      </Typography>
    </div>
  );
};

const TlcAmcSwitchToMedecinGeneraliste = () => {
  const { t } = useTranslation();
  const { data: tlcAmcSpecialties } = useTlcAmcSpecialties();
  const videoSession: VideoSessionOutputDTO = useSelector((state: any) =>
    get(state, 'videoSessions.item'),
  );

  const [isAllowedToSwitch, setIsAllowedToSwitch] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const medecinGeneralisteSpeciality = tlcAmcSpecialties?.items.find(
    speciality => speciality.shortName === 'medecin-generaliste',
  );

  const isMedecinGeneraliste =
    medecinGeneralisteSpeciality?.id === videoSession?.specialityId;

  const checkSwitchToMedecinGeneraliste = React.useCallback(() => {
    if (
      !videoSession?.creationDate ||
      isMedecinGeneraliste ||
      !medecinGeneralisteSpeciality?.id
    ) {
      return false;
    }

    const now = dayjs();
    const creationDate = dayjs(videoSession.creationDate);
    const switchMessageDate = creationDate.add(
      ALLOW_SWITCH_TO_MEDECIN_GENERALISTE_AFTER_X_MIN,
      'minute',
    );
    return now.isAfter(switchMessageDate);
  }, [
    videoSession?.creationDate,
    isMedecinGeneraliste,
    medecinGeneralisteSpeciality?.id,
  ]);

  const putSpecialityByVideoSessionId = usePutSpecialityByVideoSessionId({
    onSuccess: () => {
      toast({
        variant: 'success',
        description: t(
          'amc-tlc-waiting-room-accept-switch-medecin-generaliste',
        ),
        closable: true,
      });
      setIsSubmitted(true);
    },
    onError: () => {
      toast({
        variant: 'error',
        description: t('error:anonymous-general-error'),
        closable: true,
      });
    },
  });

  /**
   *  setInterval to enable switching to medecin-generaliste
   *  */
  React.useEffect(() => {
    // if we refresh the page after "x" mins we update the value and avoid setInterval initialization
    if (checkSwitchToMedecinGeneraliste()) {
      setIsAllowedToSwitch(true);
      return;
    }

    const interval = setInterval(() => {
      setIsAllowedToSwitch(checkSwitchToMedecinGeneraliste());
    }, 10000);
    return () => clearInterval(interval);
  }, [checkSwitchToMedecinGeneraliste]);

  const handleRefuse = () => {
    toast({
      variant: 'success',
      description: t('amc-tlc-waiting-room-refuse-switch-medecin-generaliste'),
      closable: true,
    });
    setIsSubmitted(true);
  };

  const handleAccept = () => {
    putSpecialityByVideoSessionId.mutate({
      videoSessionId: videoSession?.id || '',
      $payload: {
        specialityId: medecinGeneralisteSpeciality?.id || '',
      },
    });
  };

  return isAllowedToSwitch && !isSubmitted ? (
    <Alert variant="info">
      <AlertTitle>
        {t('amc-tlc-waiting-room-redirect-message-title')}
      </AlertTitle>
      <AlertDescription>
        {t('amc-tlc-waiting-room-redirect-message-subtitle')}
      </AlertDescription>
      <div className="flex flex-row justify-start gap-2 pt-2">
        <Button variant="ghost" color="neutral" onClick={handleRefuse}>
          {t('decline')}
        </Button>
        <Button variant="secondary" color="information" onClick={handleAccept}>
          {t('accept')}
        </Button>
      </div>
    </Alert>
  ) : null;
};

const Content = ({
  cardHcd,
  appointment,
  hideAvatar,
  size,
  videoSessionId,
}: Omit<Props, 'isAmcTlc'>) => {
  const { t } = useTranslation();
  const { data: waitingPositionData } = useGetVideoSessionWaitingPosition({
    videoSessionId,
  });

  const avatar = getAvatar({ cardHcd });
  const { isSubstitute, substitute } = getSubstitute(appointment);
  const fullName = isSubstitute
    ? substitute.fullName
    : getCardFullName(t, cardHcd);

  return (
    <div>
      <Heading
        variant="subtitle-3"
        as="h1"
        className={cn(
          'text-center text-[--kiiwi-kiiwi-450]',
          size === 'large' &&
            'text-lg [&_.highlight]:text-3xl [&_.highlight]:font-bold',
        )}
      >
        <Trans
          i18nKey="doctor-put-you-in-waiting-room"
          values={{
            fullName,
          }}
          components={{
            highlight: <span className="highlight" />,
            br: <br />,
          }}
        />
      </Heading>
      {!hideAvatar && (
        <div
          className={cn(
            'flex justify-center py-2',
            size === 'large' && 'py-5 [&_.avatar]:w-[50%] [&_.avatar]:pb-[50%]',
          )}
        >
          <Avatar
            source={isSubstitute ? substitute.avatar : avatar}
            size="medium"
          />
        </div>
      )}
      <Typography className="text-center text-neutral-primary">
        <Trans
          i18nKey="waiting-patient-count"
          components={{ strong: <strong /> }}
          values={{
            count: waitingPositionData?.positionIndex ?? 0,
          }}
        />
      </Typography>
    </div>
  );
};

const WaitingCard = ({
  cardHcd,
  appointment,
  isAmcTlc = false,
  hideDelay = false,
  hideAvatar = false,
  isMiniOpenTok = false,
  size = 'default',
  videoSessionId,
}: Props) => {
  const delay = !isAmcTlc && getStringDelay(appointment);
  const showDelay = !hideDelay && getIsDelayed(appointment);

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        size === 'large' && 'w-full max-w-[570px]',
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-2 rounded-lg bg-white p-2',
          size === 'large' && 'w-full border border-neutral p-4',
        )}
      >
        {showDelay && delay && (
          <Alert variant="warning">
            <AlertTitle className="font-thin">
              <Trans
                i18nKey="pro-delay"
                components={{ strong: <span className="font-bold" /> }}
                values={{
                  delay,
                }}
              />
            </AlertTitle>
          </Alert>
        )}
        {isAmcTlc ? (
          <TlcAmcContent hideAvatar={hideAvatar} size={size} />
        ) : (
          <Content
            cardHcd={cardHcd}
            appointment={appointment}
            hideAvatar={hideAvatar}
            size={size}
            videoSessionId={videoSessionId}
          />
        )}
      </div>
      {!isMiniOpenTok && <TlcAmcSwitchToMedecinGeneraliste />}
    </div>
  );
};

export default WaitingCard;
